import {
  SearchShipmentsForExportDocument,
  SearchShipmentsForExportWithCustomerDetailsDocument,
  SearchShipmentsWithGoodsHoldersForExportDocument,
  useSearchShipmentsQuery,
  useSearchShipmentsWithCustomerDetailsLazyQuery,
} from '../../../generated/graphql'
import { ApolloResult } from '../../../common/ApolloResult'
import { PaginatedTable } from '../../../common/components/table/PaginatedTable'
import { Snack } from '@nike/eds'
import { shipmentsTableColumns } from './ShipmentsTableColumns'
import { useEffect, useMemo } from 'react'
import {
  shipmentByGoodsHolderXlsxColumns,
  shipmentByDeliveryNoteXlsxColumns,
  shipmentByPacklistXlsxColumns,
} from './ShipmentsXlsxColumns'
import { useQueryParameters } from '../../../common/hooks/QueryParamsHook'
import '../Shipment.scss'
import { IsCarriersLoading, useCarriers } from '../../../services/CarriersProvider'
import {
  ExportButtonWithOptions,
  ExportOption,
} from '../../../common/components/export/ExportButtonWithOptions'
import { get } from '../../../common/components/properties/Properties'
import useRoleContext from '../../../common/hooks/useRoleContext'

export function ShipmentsTable() {
  const hasCustomerDetailsAccess = useRoleContext().hasCustomerDetailsAccess()
  const [{ paging, searchParameters }] = useQueryParameters()

  const queryResult = useSearchShipmentsQuery({
    variables: {
      ...searchParameters,
      search: searchParameters.search?.join(' '),
      from: paging.from,
      size: paging.size,
    },
    fetchPolicy: 'no-cache',
    context: {
      page: 'shipments',
      debounceKey: 'shipmentsSearch',
    },
  })

  const [fetchShipmentsWithCustomerDetails, queryResultWithCustomerDetails] =
    useSearchShipmentsWithCustomerDetailsLazyQuery({
      variables: {
        ...searchParameters,
        search: searchParameters.search?.join(' '),
        from: paging.from,
        size: paging.size,
      },
      fetchPolicy: 'no-cache',
      context: {
        page: 'shipments',
        debounceKey: 'shipmentsSearch',
      },
    })

  useEffect(() => {
    if (hasCustomerDetailsAccess) {
      fetchShipmentsWithCustomerDetails()
    }
  }, [hasCustomerDetailsAccess, fetchShipmentsWithCustomerDetails])

  const shipments = hasCustomerDetailsAccess
    ? queryResultWithCustomerDetails.data?.searchShipments
    : queryResult.data?.searchShipments
  const columns = useMemo(() => shipmentsTableColumns, [])
  const disabled = IsCarriersLoading()
  const [carriers] = useCarriers()

  function mapShipmentsByDeliveryNote(data) {
    let newData = []
    data.forEach((shipment) => {
      const deliveryNotes = get(shipment, 'deliveries.deliveryNoteNumber') || []
      deliveryNotes.forEach((dn) =>
        newData.push({
          deliveryNoteNumber: dn,
          ...shipment,
        })
      )
    })
    return newData
  }

  function mapShipmentsByGoodsHolder(data) {
    let newData = []
    data.forEach((shipment) => {
      const goodsHolders = get(shipment, 'goodsHolders') || []
      goodsHolders.forEach((gh) =>
        newData.push({
          goodsHolderNumber: gh.goodsHolderNumber,
          goodsHolderTrackingNumber: gh.goodsHolderTrackingNumber,
          calculatedGoodsHolderStatusCode: gh.calculatedGoodsHolderStatusCode,
          salesOrderNumber: gh.salesOrderNumber,
          purchaseOrderNumber: gh.purchaseOrderNumber,
          deliveryNoteNumber: get(gh, 'goodsHolderItems.deliveryNoteNumber'),
          calculatedPlannedQuantity: gh.calculatedPlannedQuantity,
          calculatedPackedQuantity: gh.calculatedPackedQuantity,
          goodsHolderMeasures: gh.goodsHolderMeasures,
          transportHolder: gh.transportHolder,
          ...shipment,
        })
      )
    })
    return newData
  }

  const exportOptions: ExportOption[] = [
    {
      label: 'Export by Packlist',
      xlsxColumns: shipmentByPacklistXlsxColumns,
      exportQuery: {
        query: hasCustomerDetailsAccess
          ? SearchShipmentsForExportWithCustomerDetailsDocument
          : SearchShipmentsForExportDocument,
        variables: { ...searchParameters, search: searchParameters.search?.join(' ') },
        name: 'searchShipments',
        action: 'exportByPacklist',
        context: {
          page: 'shipments',
        },
      },
      fileName: 'shipments-by-packlist.xlsx',
    },
    {
      label: 'Export by Delivery Note',
      xlsxColumns: shipmentByDeliveryNoteXlsxColumns,
      exportQuery: {
        query: hasCustomerDetailsAccess
          ? SearchShipmentsForExportWithCustomerDetailsDocument
          : SearchShipmentsForExportDocument,
        variables: { ...searchParameters, search: searchParameters.search?.join(' ') },
        name: 'searchShipments',
        action: 'exportByDeliveryNote',
        context: {
          page: 'shipments',
        },
      },
      format: mapShipmentsByDeliveryNote,
      fileName: 'shipments-by-delivery-note.xlsx',
    },
    {
      label: 'Export by Carton',
      xlsxColumns: shipmentByGoodsHolderXlsxColumns,
      exportQuery: {
        query: SearchShipmentsWithGoodsHoldersForExportDocument,
        variables: { ...searchParameters, search: searchParameters.search?.join(' ') },
        name: 'searchShipmentsWithGoodsHolders',
        action: 'exportByCarton',
        context: {
          page: 'shipments',
        },
      },
      format: mapShipmentsByGoodsHolder,
      fileName: 'shipments-by-carton.xlsx',
      pageSize: 100,
    },
  ]

  return (
    <ApolloResult {...queryResult}>
      <div className="table-title-row">
        <ExportButtonWithOptions
          exportOptions={exportOptions}
          extraData={carriers}
          disabled={disabled}
          className="table-export-button"
        />
      </div>
      {shipments?.length > 0 ? (
        <PaginatedTable
          columns={columns}
          data={shipments}
          paging={paging}
          className="shipments-table"
        />
      ) : (
        <Snack id="no-cartons-snack" onDismiss={() => {}} hideDismiss={true} status="info">
          No shipments found
        </Snack>
      )}
    </ApolloResult>
  )
}
